import React, { useState } from 'react';
import { Rack, RackType, ServerType, TORSwitchType, ServerAgeStage, PendingDelivery } from '../types';
import { SERVER_TYPES, TOR_SWITCH_TYPES, MPO_PANEL_TYPES, MGMT_SWITCH_TYPES, SERVER_UNLOCK_THRESHOLDS, CONTRACT_TYPES, SERVER_REPAIR_BASE_COST_MULTIPLIER, SERVER_REPAIR_TICKS, POWER_SUPPLIERS, SERVER_AGING_MATURE_TICKS, SERVER_AGING_OLD_TICKS, SERVER_AGING_EOL_TICKS } from '../constants';
import RackInteriorView, { RACK_SERVER_MAX_U } from './RackInteriorView';

const AGE_STAGE_LABEL: Record<ServerAgeStage, { label: string; cls: string }> = {
  new: { label: 'New', cls: 'text-green-400' },
  mature: { label: 'Mature', cls: 'text-yellow-300' },
  aging: { label: 'Aging', cls: 'text-orange-400' },
  eol: { label: 'EOL', cls: 'text-red-400' },
};

// Reusable install card for network equipment (MPO / TOR / redundant TOR / mgmt switch)
const NetworkInstallCard: React.FC<{
  name: string;
  description: string;
  cost: number;
  powerDrawKW?: number;
  chips: React.ReactNode;
  money: number;
  rackFreePower: number;
  disabledReason?: string; // hard prerequisite not met (e.g. MPO panel too slow)
  ctaLabel?: string;       // button verb when installable (default "Install")
  onInstall: () => void;
}> = ({ name, description, cost, powerDrawKW = 0, chips, money, rackFreePower, disabledReason, ctaLabel = 'Install', onInstall }) => {
  const hasMoney = money >= cost;
  const hasRackPower = rackFreePower >= powerDrawKW;
  const canInstall = hasMoney && hasRackPower && !disabledReason;
  return (
    <div className="flex items-center justify-between bg-gray-700 p-3 rounded">
      <div>
        <div className="font-medium text-sm">{name}</div>
        <div className="text-xs text-gray-400">{description}</div>
        <div className="mt-1 flex gap-3 text-xs flex-wrap">
          <span className="text-green-400">${cost.toLocaleString()}</span>
          {chips}
        </div>
      </div>
      <button
        onClick={canInstall ? onInstall : undefined}
        disabled={!canInstall}
        className={`px-3 py-1 rounded text-sm whitespace-nowrap ${canInstall ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-gray-600 text-gray-400 cursor-not-allowed'}`}
        title={disabledReason || (!hasMoney ? 'Not enough money' : !hasRackPower ? 'Not enough PDU budget' : 'Install')}
        type="button"
      >
        {disabledReason ? '🔒 Locked' : !hasMoney ? '💰 No funds' : !hasRackPower ? '⚡ PDU full' : `✅ ${ctaLabel}`}
      </button>
    </div>
  );
};

interface ServerManagementProps {
  selectedRack: number | null;
  selectedRackObj: Rack | null;
  selectedRackType: RackType | null;
  selectedRackUsedPower: number;
  money: number;
  currentLocation: string;
  buyRack: (locationId: string, rackId: number | null, rackTypeId: string) => void;
  installServer: (locationId: string, rackId: number | null, serverTypeId: string) => void;
  fillRack: (locationId: string, rackId: number, serverTypeId: string) => number;
  installTORSwitch: (locationId: string, rackId: number | null, switchTypeId: string) => void;
  installMpoPatchPanel: (locationId: string, rackId: number | null, panelTypeId: string) => void;
  upgradeMpoPatchPanel: (locationId: string, rackId: number | null, panelTypeId: string) => void;
  installRedundantTorSwitch: (locationId: string, rackId: number | null, switchTypeId: string) => void;
  installManagementSwitch: (locationId: string, rackId: number | null, switchTypeId: string) => void;
  removeServer: (locationId: string, rackId: number, serverId: string) => void;
  removeRack: (locationId: string, rackId: number) => void;
  repairServer: (locationId: string, rackId: number, serverId: string) => void;
  gameTime: number;
  rackTypes: RackType[];
  serverTypes: ServerType[];
  torSwitchTypes: TORSwitchType[];
  availablePower: number;
  playerLevel: number;
  formatCurrency: (amount: number, decimals?: number) => string;
  reputation?: number;
  powerEfficiencyUpgrade?: number;
  hasTransformer: boolean;
  hasPowerhouse: boolean;
  hasUps: boolean;
  hasPowerSupplier: boolean;
  hasCooling: boolean;
  availableCooling: number;
  locationConnectivity: number; // 0–1 fraction of server traffic covered by external links
  networkStatus?: { mmrBuilt: boolean; locationHasCore: boolean; floorHasHda: boolean; externalConnectivity: number };
  thermalStrain?: number;
  isPaused?: boolean;
  selectedPowerSupplierId?: string;
  serverPerformanceUpgrade?: number; // upgrade level (0–10)
  ixpBonusFraction?: number; // additive revenue bonus from IXP connections (0–0.20)
  activeContracts?: import('../types').ActiveContract[];
  pendingDeliveries?: PendingDelivery[];
  gameDay?: number;
}

const ServerManagement: React.FC<ServerManagementProps> = ({
  selectedRack,
  selectedRackObj,
  selectedRackType,
  selectedRackUsedPower,
  money,
  currentLocation,
  hasTransformer,
  hasPowerhouse,
  hasUps,
  hasPowerSupplier,
  hasCooling,
  availableCooling,
  locationConnectivity,
  networkStatus,
  thermalStrain,
  isPaused = false,
  buyRack,
  installServer,
  fillRack,
  installTORSwitch,
  installMpoPatchPanel,
  upgradeMpoPatchPanel,
  installRedundantTorSwitch,
  installManagementSwitch,
  removeServer,
  removeRack,
  repairServer,
  gameTime,
  rackTypes,
  serverTypes,
  torSwitchTypes,
  availablePower,
  playerLevel = 0,
  formatCurrency,
  reputation = 100,
  powerEfficiencyUpgrade = 0,
  selectedPowerSupplierId,
  serverPerformanceUpgrade = 0,
  ixpBonusFraction = 0,
  activeContracts = [],
  pendingDeliveries = [],
  gameDay = 0,
}) => {
  const [deliveriesExpanded, setDeliveriesExpanded] = useState(true);
  const powerCostPerKwh = (() => {
    if (!selectedPowerSupplierId) return 0.12;
    const supplier = POWER_SUPPLIERS.find(s => s.id === selectedPowerSupplierId);
    return supplier?.costPerKWh ?? 0.12;
  })();
  // Compute used U-slots from installed servers
  const usedU = selectedRackObj?.servers?.reduce((sum, s) => {
    const st = SERVER_TYPES.find(t => t.id === s.type);
    return sum + (st?.uSize ?? 1);
  }, 0) ?? 0;

  const freeU = RACK_SERVER_MAX_U - usedU;
  const uPct = Math.min(100, (usedU / RACK_SERVER_MAX_U) * 100);
  const uBarColor = uPct >= 95 ? 'bg-red-500' : uPct >= 75 ? 'bg-yellow-500' : 'bg-green-500';

  const installedSwitch = selectedRackObj?.torSwitch
    ? TOR_SWITCH_TYPES.find(s => s.id === selectedRackObj.torSwitch!.typeId)
    : null;

  const serverCount = selectedRackObj?.servers?.length ?? 0;
  const portCount = installedSwitch?.portCount ?? 0;
  const freePorts = Math.max(0, portCount - serverCount);
  const portPct = portCount > 0 ? Math.min(100, (serverCount / portCount) * 100) : 0;
  const portBarColor = portPct >= 95 ? 'bg-red-500' : portPct >= 75 ? 'bg-yellow-500' : 'bg-green-500';

  // Deliveries at this location
  const locationDeliveries = pendingDeliveries.filter(d => d.locationId === currentLocation);

  return (
    <div className="lg:col-span-1">
      {/* ── Pending Deliveries Panel ─────────────────────────────────── */}
      {locationDeliveries.length > 0 && (
        <div className="bg-gray-800 border border-amber-700/50 rounded-lg mb-4 overflow-hidden">
          <button
            onClick={() => setDeliveriesExpanded(p => !p)}
            className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-semibold text-amber-300 hover:bg-gray-700/40 transition-colors"
          >
            <span>📦 Pending Deliveries ({locationDeliveries.length})</span>
            <span className="text-gray-500 text-xs">{deliveriesExpanded ? '▲' : '▼'}</span>
          </button>
          {deliveriesExpanded && (
            <div className="divide-y divide-gray-700/60">
              {locationDeliveries.map(d => {
                const st = SERVER_TYPES.find(s => s.id === d.serverTypeId);
                const daysLeft = Math.max(0, d.deliveryAtDay - gameDay);
                const totalDays = d.deliveryAtDay - d.orderedAtDay;
                const progress = totalDays > 0 ? Math.min(1, 1 - daysLeft / totalDays) : 1;
                return (
                  <div key={d.id} className="px-4 py-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-white">{st?.name ?? 'Server'}</span>
                      <span className={`text-xs font-semibold ${daysLeft === 0 ? 'text-emerald-400' : 'text-amber-400'}`}>
                        {daysLeft === 0 ? 'Arriving today' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-1 mb-1">
                      <div
                        className="bg-amber-500 h-1 rounded-full transition-all"
                        style={{ width: `${progress * 100}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-500">
                      Floor {d.floorId} · Rack #{d.rackId} · Day {d.orderedAtDay} → {d.deliveryAtDay}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <h2 className="text-xl font-bold mb-4">Rack Management</h2>
      {selectedRack && (
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-bold">{selectedRackType?.name || 'Empty Rack'}</h3>
            <div className="flex gap-2">
              {selectedRackType && (selectedRackObj?.servers ?? []).length > 0 && selectedRack !== null && (() => {
                const servers = selectedRackObj?.servers ?? [];
                const totalSalvage = servers.reduce((sum, s) => {
                  const st = SERVER_TYPES.find(t => t.id === s.type);
                  return sum + Math.floor((st?.cost ?? 0) * 0.5);
                }, 0);
                return (
                  <button
                    onClick={() => {
                      if (!window.confirm(`Decommission all ${servers.length} servers? Recovers $${totalSalvage.toLocaleString()} (50% salvage each).`)) return;
                      [...servers].forEach(s => removeServer(currentLocation, selectedRack, s.id));
                    }}
                    className="px-2.5 py-1 rounded bg-red-900/60 hover:bg-red-700 text-red-300 hover:text-white transition-colors text-xs font-semibold"
                    title={`Decommission all servers — recover $${totalSalvage.toLocaleString()} total`}
                  >
                    Decommission All (+${totalSalvage.toLocaleString()})
                  </button>
                );
              })()}
              {selectedRackType && (selectedRackObj?.servers ?? []).length === 0 && selectedRack !== null && (() => {
                const salvage = Math.floor((selectedRackType.cost ?? 0) * 0.4);
                return (
                  <button
                    onClick={() => removeRack(currentLocation, selectedRack)}
                    className="px-2.5 py-1 rounded bg-red-900/60 hover:bg-red-700 text-red-300 hover:text-white transition-colors text-xs font-semibold"
                    title={`Sell rack — recover $${salvage.toLocaleString()} (40% of purchase price)`}
                  >
                    Sell Rack (+${salvage.toLocaleString()})
                  </button>
                );
              })()}
            </div>
          </div>
          <p className="text-sm text-gray-400 mb-2">{selectedRackType?.description || 'Select a rack to purchase.'}</p>

          {/* Rack status bars — only when a rack type is installed */}
          {selectedRackType && (() => {
            const rackPowerBudget = selectedRackType.power;
            const rackPowerUsed = selectedRackUsedPower;
            const rackCoolingUsed = selectedRackObj?.usedCooling ?? 0;
            const powerPct = rackPowerBudget > 0 ? Math.min(100, (rackPowerUsed / rackPowerBudget) * 100) : 0;
            const powerBarColor = powerPct >= 95 ? 'bg-red-500' : powerPct >= 80 ? 'bg-yellow-500' : 'bg-green-500';
            return (
              <div className="space-y-3 mb-4">
                {/* U-height bar */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-400">Rack units (U):</span>
                    <span className={uPct >= 95 ? 'text-red-400 font-semibold' : 'text-white'}>
                      {usedU}U / {RACK_SERVER_MAX_U}U used — {freeU}U free
                    </span>
                  </div>
                  <div className="w-full bg-gray-600 rounded-full h-2">
                    <div className={`${uBarColor} h-2 rounded-full transition-all`} style={{ width: `${uPct}%` }} />
                  </div>
                </div>
                {/* Power bar */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-400">Power budget:</span>
                    <span className={powerPct >= 95 ? 'text-red-400 font-semibold' : 'text-white'}>
                      {rackPowerUsed.toFixed(2)} / {rackPowerBudget} kW
                    </span>
                  </div>
                  <div className="w-full bg-gray-600 rounded-full h-2">
                    <div className={`${powerBarColor} h-2 rounded-full transition-all`} style={{ width: `${powerPct}%` }} />
                  </div>
                </div>
                {/* Cooling */}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Cooling used:</span>
                  <span>{rackCoolingUsed.toFixed(2)} kW</span>
                </div>
                {/* TOR switch status */}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">TOR switch:</span>
                  {installedSwitch
                    ? <span className="text-green-400">{installedSwitch.name} ({installedSwitch.uplinkBandwidthGbps}G uplink)</span>
                    : <span className="text-orange-400">Not installed</span>
                  }
                </div>
                {/* Switch port usage */}
                {installedSwitch && (
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-400">Network ports:</span>
                      <span className={portPct >= 95 ? 'text-red-400 font-semibold' : 'text-white'}>
                        {serverCount} / {portCount} ports used — {freePorts} free
                      </span>
                    </div>
                    <div className="w-full bg-gray-600 rounded-full h-2">
                      <div className={`${portBarColor} h-2 rounded-full transition-all`} style={{ width: `${portPct}%` }} />
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Network section — informational status only (buying happens in the cards below) */}
          {selectedRackObj && selectedRackType && (() => {
            const mpo = selectedRackObj.mpoPatchPanel
              ? MPO_PANEL_TYPES.find(p => p.id === (selectedRackObj.mpoPatchPanel as any).typeId)
              : null;
            const primarySwitch = selectedRackObj.torSwitch
              ? TOR_SWITCH_TYPES.find(s => s.id === (selectedRackObj.torSwitch as any).typeId)
              : null;
            const redundantSwitch = selectedRackObj.torSwitchRedundant
              ? TOR_SWITCH_TYPES.find(s => s.id === (selectedRackObj.torSwitchRedundant as any).typeId)
              : null;
            const mgmtSwitch = selectedRackObj.managementSwitch
              ? MGMT_SWITCH_TYPES.find(s => s.id === (selectedRackObj.managementSwitch as any).typeId)
              : null;
            const uplinkGbps = primarySwitch ? primarySwitch.uplinkBandwidthGbps * (redundantSwitch ? 2 : 1) : 0;
            const usedTrafficGbps = (selectedRackObj.servers ?? [])
              .filter((s: any) => s.failureStatus !== 'failed')
              .reduce((sum: number, s: any) => {
                const st = SERVER_TYPES.find(t => t.id === s.type);
                return sum + (st?.trafficGbps ?? 0);
              }, 0);
            const bwPct = uplinkGbps > 0 ? Math.min(100, (usedTrafficGbps / uplinkGbps) * 100) : 0;
            const bwBarColor = bwPct >= 90 ? 'bg-red-500' : bwPct >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
            const isSaturated = bwPct >= 100;

            const Row = ({ slot, label, value }: { slot: string; label: string; value: React.ReactNode }) => (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">{slot} — {label}:</span>
                <span>{value}</span>
              </div>
            );

            return (
              <div className="mb-4 border border-gray-600/50 rounded p-3 bg-gray-800/40 space-y-2">
                <h4 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
                  Network Status
                  {isSaturated && <span className="text-[10px] bg-red-800 text-red-300 px-1.5 py-0.5 rounded font-bold">SATURATED</span>}
                </h4>

                <Row slot="U48" label="MPO panel" value={mpo
                  ? <span className="text-emerald-400">{mpo.name} ({mpo.maxUplinkGbps}G max)</span>
                  : <span className="text-orange-400">Not installed</span>} />
                <Row slot="U46" label="Primary TOR" value={primarySwitch
                  ? <span className="text-emerald-400">{primarySwitch.name} ({primarySwitch.uplinkBandwidthGbps}G uplink)</span>
                  : <span className="text-orange-400">Not installed</span>} />
                <Row slot="U45" label="Redundant TOR" value={redundantSwitch
                  ? <span className="text-emerald-400">{redundantSwitch.name} <span className="text-xs text-indigo-400">(MLAG active)</span></span>
                  : <span className="text-gray-500">Not installed (optional)</span>} />
                <Row slot="U44" label="Mgmt Switch" value={mgmtSwitch
                  ? <span className="text-emerald-400">{mgmtSwitch.name} <span className="text-xs text-indigo-400">(remote mgmt)</span></span>
                  : <span className="text-amber-400">Not installed</span>} />
                {!mgmtSwitch && (selectedRackObj.servers?.length ?? 0) > 0 && (
                  <p className="text-[11px] text-amber-400">No remote management — technicians can't auto-repair this rack and manual repairs are ~80% slower (on-site dispatch).</p>
                )}

                {/* Bandwidth bar */}
                <div className="pt-1">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-400">Bandwidth{redundantSwitch ? ' (MLAG ×2)' : ''}:</span>
                    <span className={bwPct >= 90 ? 'text-red-400 font-semibold' : 'text-white'}>
                      {usedTrafficGbps.toFixed(1)} / {uplinkGbps > 0 ? `${uplinkGbps}` : '—'} Gbps
                    </span>
                  </div>
                  {uplinkGbps > 0 && (
                    <div className="w-full bg-gray-600 rounded-full h-2">
                      <div className={`${bwBarColor} h-2 rounded-full transition-all`} style={{ width: `${bwPct}%` }} />
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Network equipment install cards — the buying process (chain order) */}
          {selectedRackObj && selectedRackObj.type && selectedRackType && (() => {
            const rackFreePower = (selectedRackType.power ?? 0) - selectedRackUsedPower;
            const mpo = selectedRackObj.mpoPatchPanel
              ? MPO_PANEL_TYPES.find(p => p.id === (selectedRackObj.mpoPatchPanel as any).typeId)
              : null;
            const primary = selectedRackObj.torSwitch
              ? TOR_SWITCH_TYPES.find(s => s.id === (selectedRackObj.torSwitch as any).typeId)
              : null;
            const redundant = !!selectedRackObj.torSwitchRedundant;
            const mgmt = !!selectedRackObj.managementSwitch;

            return (
              <>
                {/* Step 1 — MPO panel */}
                {!mpo && (
                  <div className="mb-4">
                    <h4 className="text-md font-semibold mb-1 text-orange-400">MPO Patch Panel Required</h4>
                    <p className="text-xs text-gray-400 mb-3">The fiber patch panel (U48) terminates uplinks. Required before a TOR switch — its tier caps the uplink speed you can run.</p>
                    <div className="space-y-2">
                      {MPO_PANEL_TYPES.map(p => (
                        <NetworkInstallCard
                          key={p.id} name={p.name} description={p.description} cost={p.cost}
                          money={money} rackFreePower={rackFreePower}
                          chips={<><span className="text-purple-400">{p.maxUplinkGbps}G max uplink</span><span className="text-blue-400">{p.fiberCount} fibers</span></>}
                          onInstall={() => installMpoPatchPanel(currentLocation, selectedRack, p.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 1b — MPO panel upgrade (unlocks faster TOR uplinks) */}
                {mpo && (() => {
                  const higher = MPO_PANEL_TYPES.filter(p => p.maxUplinkGbps > mpo.maxUplinkGbps);
                  if (higher.length === 0) return null;
                  return (
                    <div className="mb-4">
                      <h4 className="text-md font-semibold mb-1 text-gray-300">Upgrade MPO Panel — Optional <span className="text-xs text-purple-400 font-normal">(unlocks faster TOR uplinks)</span></h4>
                      <p className="text-xs text-gray-400 mb-3">Current: {mpo.name} ({mpo.maxUplinkGbps}G max). Upgrading charges only the price difference.</p>
                      <div className="space-y-2">
                        {higher.map(p => (
                          <NetworkInstallCard
                            key={p.id} name={p.name} description={p.description} cost={Math.max(0, p.cost - mpo.cost)}
                            money={money} rackFreePower={rackFreePower} ctaLabel="Upgrade"
                            chips={<><span className="text-purple-400">{p.maxUplinkGbps}G max uplink</span><span className="text-gray-400">from {mpo.maxUplinkGbps}G</span></>}
                            onInstall={() => upgradeMpoPatchPanel(currentLocation, selectedRack, p.id)}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Step 2 — Primary TOR */}
                {mpo && !primary && (
                  <div className="mb-4">
                    <h4 className="text-md font-semibold mb-1 text-orange-400">TOR Switch Required</h4>
                    <p className="text-xs text-gray-400 mb-3">A Top-of-Rack switch (U46) must be installed before servers can be added. It provides network connectivity and counts against the rack's PDU budget.</p>
                    <div className="space-y-2">
                      {torSwitchTypes.map(sw => {
                        const tooFast = mpo.maxUplinkGbps < sw.uplinkBandwidthGbps;
                        return (
                          <NetworkInstallCard
                            key={sw.id} name={sw.name} description={sw.description} cost={sw.cost} powerDrawKW={sw.powerDrawKW}
                            money={money} rackFreePower={rackFreePower}
                            disabledReason={tooFast ? `MPO panel only supports ${mpo.maxUplinkGbps}G — upgrade the panel first` : undefined}
                            chips={<><span className="text-blue-400">{sw.portCount} ports</span><span className="text-purple-400">{sw.uplinkBandwidthGbps}G uplink</span><span className="text-red-400">{sw.powerDrawKW} kW</span></>}
                            onInstall={() => installTORSwitch(currentLocation, selectedRack, sw.id)}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Step 3 — Redundant TOR (optional) */}
                {primary && !redundant && (
                  <div className="mb-4">
                    <h4 className="text-md font-semibold mb-1 text-gray-300">Redundant TOR — Optional <span className="text-xs text-indigo-400 font-normal">(MLAG: doubles uplink + survives switch failure)</span></h4>
                    <div className="space-y-2">
                      {torSwitchTypes.map(sw => (
                        <NetworkInstallCard
                          key={sw.id} name={sw.name} description={sw.description} cost={sw.cost} powerDrawKW={sw.powerDrawKW}
                          money={money} rackFreePower={rackFreePower}
                          chips={<><span className="text-blue-400">{sw.portCount} ports</span><span className="text-purple-400">+{sw.uplinkBandwidthGbps}G</span><span className="text-red-400">{sw.powerDrawKW} kW</span></>}
                          onInstall={() => installRedundantTorSwitch(currentLocation, selectedRack, sw.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 4 — Management switch (recommended) */}
                {!mgmt && (
                  <div className="mb-4">
                    <h4 className="text-md font-semibold mb-1 text-amber-400">Management Switch — Recommended <span className="text-xs text-gray-400 font-normal">(OOB / iDRAC: enables remote repair)</span></h4>
                    <p className="text-xs text-gray-400 mb-3">Without an out-of-band management switch (U44), technicians can't auto-repair this rack and manual repairs take ~80% longer.</p>
                    <div className="space-y-2">
                      {MGMT_SWITCH_TYPES.map(sw => (
                        <NetworkInstallCard
                          key={sw.id} name={sw.name} description={sw.description} cost={sw.cost} powerDrawKW={sw.powerDrawKW}
                          money={money} rackFreePower={rackFreePower}
                          chips={<><span className="text-blue-400">{sw.managedPorts} managed ports</span><span className="text-red-400">{sw.powerDrawKW} kW</span></>}
                          onInstall={() => installManagementSwitch(currentLocation, selectedRack, sw.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          {/* Persistent connectivity warning — shown whenever servers exist but connectivity is 0 */}
          {locationConnectivity === 0 && (selectedRackObj?.servers?.length ?? 0) > 0 && (
            <div className="mb-3 p-2 rounded bg-red-900/50 border border-red-700 text-red-300 text-xs">
              <strong>No internet connectivity — all servers earn $0.</strong> Build the MMR then order a transit link or connect to an IXP to carry customer traffic.
            </div>
          )}
          {locationConnectivity > 0 && locationConnectivity < 0.5 && (selectedRackObj?.servers?.length ?? 0) > 0 && (
            <div className="mb-3 p-2 rounded bg-amber-900/40 border border-amber-700 text-amber-300 text-xs">
              <strong>Transit underprovisioned ({Math.round(locationConnectivity * 100)}%).</strong> Revenue is cut proportionally. Order more bandwidth in Connectivity → Transit.
            </div>
          )}

          {/* Rack interior view — shown whenever a rack type is installed */}
          {selectedRackObj && selectedRackObj.type !== null && selectedRackType && (
            <RackInteriorView
              rack={selectedRackObj}
              rackType={selectedRackType}
              gameTime={gameTime}
              onRepair={(serverId) => repairServer(currentLocation, selectedRack!, serverId)}
              formatCurrency={formatCurrency}
              networkStatus={networkStatus}
              thermalStrain={thermalStrain}
              isPaused={isPaused}
            />
          )}

          {/* Empty rack — show rack purchase options */}
          {selectedRackObj && selectedRackObj.type === null && (
            <div className="mt-4">
              <h4 className="text-md font-semibold mb-2">Available Rack Types</h4>
              <div className="space-y-3">
                {rackTypes.map(rackType => (
                  <div key={rackType.id} className="flex items-center justify-between bg-gray-700 p-3 rounded">
                    <div>
                      <div className="font-medium">{rackType.name}</div>
                      <div className="text-xs text-gray-400">{rackType.description}</div>
                      <div className="mt-1 flex gap-3 text-xs flex-wrap">
                        <span className="text-green-400">${rackType.cost.toLocaleString()}</span>
                        <span className="text-blue-400">48U</span>
                        <span className="text-red-400">PDU: {rackType.power} kW</span>
                        <span className="text-cyan-400">Cooling: {rackType.cooling} kW</span>
                      </div>
                    </div>
                    {(() => {
                      const hasPowerChain = hasTransformer && hasPowerhouse && hasUps && hasPowerSupplier;
                      const hasEnoughPower = hasPowerChain && availablePower >= 0.1;
                      const hasEnoughCooling = hasCooling && availableCooling >= 0.1;
                      const hasEnoughMoney = money >= rackType.cost;
                      const canPurchase = hasEnoughMoney && hasEnoughPower && hasEnoughCooling;

                      let buttonText = 'Purchase';
                      let buttonClass = 'px-3 py-1 rounded text-sm ';
                      if (!hasTransformer) { buttonText = '⚠️ Need Transformer'; buttonClass += 'bg-orange-600 text-white cursor-not-allowed'; }
                      else if (!hasPowerhouse) { buttonText = '⚠️ Need Powerhouse'; buttonClass += 'bg-orange-600 text-white cursor-not-allowed'; }
                      else if (!hasUps) { buttonText = '⚠️ Need UPS'; buttonClass += 'bg-orange-600 text-white cursor-not-allowed'; }
                      else if (!hasPowerSupplier) { buttonText = '⚠️ Need Power Supplier'; buttonClass += 'bg-orange-600 text-white cursor-not-allowed'; }
                      else if (!hasCooling) { buttonText = '❄️ Need Cooling Unit'; buttonClass += 'bg-blue-600 text-white cursor-not-allowed'; }
                      else if (!hasEnoughCooling) { buttonText = '❄️ Need more cooling'; buttonClass += 'bg-blue-600 text-white cursor-not-allowed'; }
                      else if (!hasEnoughPower) { buttonText = '⚡ Need more power'; buttonClass += 'bg-red-600 text-white cursor-not-allowed'; }
                      else if (!hasEnoughMoney) { buttonText = '💰 No Money'; buttonClass += 'bg-gray-600 text-white cursor-not-allowed'; }
                      else { buttonText = '✅ Purchase'; buttonClass += 'bg-green-600 hover:bg-green-700 text-white'; }

                      return (
                        <button
                          onClick={canPurchase ? () => buyRack(currentLocation, selectedRack, rackType.id) : undefined}
                          disabled={!canPurchase}
                          className={buttonClass}
                          type="button"
                        >
                          {buttonText}
                        </button>
                      );
                    })()}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Occupied rack — TOR switch required before servers */}
          {selectedRackObj && selectedRackObj.type !== null && (
            <div className="mt-4">
              {/* Installed servers list with satisfaction + aging */}
              {(selectedRackObj?.servers ?? []).filter(s => s.failureStatus !== 'failed' && s.failureStatus !== 'repairing').length > 0 && (
                <div className="mb-4 border border-gray-600/50 rounded p-3 bg-gray-800/40">
                  <h4 className="text-sm font-semibold mb-2 text-gray-300">Running Servers ({(selectedRackObj?.servers ?? []).filter(s => s.failureStatus !== 'failed' && s.failureStatus !== 'repairing').length})</h4>
                  <div className="space-y-1.5">
                    {(selectedRackObj?.servers ?? [])
                      .filter(s => s.failureStatus !== 'failed' && s.failureStatus !== 'repairing')
                      .map(server => {
                        const st = SERVER_TYPES.find(t => t.id === server.type);
                        const satisfaction = server.satisfactionScore ?? 100;
                        const satColor = satisfaction >= 70 ? 'text-green-400' : satisfaction >= 40 ? 'text-yellow-400' : 'text-red-400';
                        const ageStage = server.ageStage ?? 'new';
                        const ageInfo = AGE_STAGE_LABEL[ageStage];
                        const eff = server.ageEfficiencyMultiplier ?? 1.0;
                        const salvage = Math.floor((st?.cost ?? 0) * 0.5);
                        const util = server.utilizationRate ?? 1.0;
                        const utilPct = Math.round(util * 100);
                        const utilColor = util > 1.15 ? 'text-red-400' : util >= 0.8 ? 'text-emerald-400' : 'text-yellow-400';
                        const liveTraffic = st ? (st.trafficGbps * util).toFixed(2) : '?';
                        const serverContract = server.contractId
                          ? activeContracts.find(c => c.active && `${c.templateId}|${c.locationId}` === server.contractId)
                          : null;
                        return (
                          <div key={server.id} className="flex items-center justify-between text-xs bg-gray-700/50 rounded px-2 py-1 gap-2">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="text-gray-200 font-medium truncate">{st?.name ?? server.type}</span>
                              {serverContract ? (
                                <span className="shrink-0 px-1.5 py-0.5 rounded bg-emerald-900/50 text-emerald-400 text-[10px] font-medium border border-emerald-700/40">
                                  {serverContract.clientName}
                                </span>
                              ) : (
                                <span className="shrink-0 px-1.5 py-0.5 rounded bg-gray-600/50 text-gray-500 text-[10px]">idle</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span title={
                                util > 1.15
                                  ? `Overloaded (${utilPct}%) — SLA risk. Distribute load across more servers.`
                                  : util >= 0.8
                                  ? `Healthy utilization (${utilPct}%) — live traffic ${liveTraffic} Gbps`
                                  : `Low utilization (${utilPct}%) — normal variation, no action needed. Servers drift between 50–115% randomly.`
                              } className={utilColor}>
                                {utilPct}% · {liveTraffic}G
                              </span>
                              <span title={(() => {
                                const reasons: string[] = [];
                                if (server.failureStatus === 'failed') reasons.push('Server is down — customers are churning');
                                else if (server.failureStatus === 'repairing') reasons.push('Server under repair — reduced capacity');
                                const age = server.ageStage ?? 'new';
                                if (age === 'aging') reasons.push('Server aging — reliability declining');
                                else if (age === 'eol') reasons.push('End-of-Life — customers noticing degraded performance');
                                const util = server.utilizationRate ?? 1.0;
                                if (util < 0.5) reasons.push('Very low utilization — customers may leave');
                                if (util > 1.5) reasons.push('Severely overloaded — SLA breaches');
                                const revenueImpact = satisfaction < 100
                                  ? `\nRevenue impact: −${((100 - satisfaction) * 0.5).toFixed(0)}% (0.5%/point below 100)`
                                  : '';
                                return reasons.length > 0
                                  ? `Satisfaction: ${satisfaction}/100\n⚠ ${reasons.join('\n⚠ ')}${revenueImpact}`
                                  : `Satisfaction: ${satisfaction}/100 — customers are happy${revenueImpact}`;
                              })()} className={satColor}>
                                ★ {satisfaction}
                              </span>
                              <span title={(() => {
                                const age = gameTime - (server.installedAt ?? 0);
                                let nextLabel = '';
                                let nextIn = 0;
                                if (ageStage === 'new') { nextLabel = 'Mature'; nextIn = SERVER_AGING_MATURE_TICKS - age; }
                                else if (ageStage === 'mature') { nextLabel = 'Aging'; nextIn = SERVER_AGING_OLD_TICKS - age; }
                                else if (ageStage === 'aging') { nextLabel = 'EOL'; nextIn = SERVER_AGING_EOL_TICKS - age; }
                                const daysLeft = nextIn > 0 ? Math.ceil(nextIn / (60 * 24)) : 0;
                                return `Age: ${ageStage}, efficiency ${Math.round(eff * 100)}%${nextLabel ? `\nNext stage (${nextLabel}) in ~${daysLeft} in-game days` : ''}`;
                              })()} className={ageInfo.cls}>
                                {ageInfo.label}{eff < 1.0 ? ` ${Math.round(eff * 100)}%` : ''}
                              </span>
                              {selectedRack !== null && (
                                <button
                                  onClick={() => removeServer(currentLocation, selectedRack, server.id)}
                                  title={`Decommission — recover $${salvage.toLocaleString()} (50% salvage)`}
                                  className="px-1.5 py-0.5 rounded bg-red-900/60 hover:bg-red-700 text-red-300 hover:text-white transition-colors text-[10px] font-semibold"
                                  type="button"
                                >
                                  Decommission
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Failed / repairing servers panel */}
              {(() => {
                const downServers = (selectedRackObj?.servers ?? []).filter(
                  s => s.failureStatus === 'failed' || s.failureStatus === 'repairing'
                );
                if (downServers.length === 0) return null;
                return (
                  <div className="mb-4 border border-red-700/60 rounded p-3 bg-red-900/20">
                    <h4 className="text-md font-semibold mb-2 text-red-400">⚠️ Hardware Issues ({downServers.length})</h4>
                    <div className="space-y-2">
                      {downServers.map(server => {
                        const st = SERVER_TYPES.find(t => t.id === server.type);
                        const repairCost = Math.round((st?.cost ?? 0) * SERVER_REPAIR_BASE_COST_MULTIPLIER);
                        const isRepairing = server.failureStatus === 'repairing';
                        const ticksLeft = isRepairing && server.repairCompletesAt !== undefined
                          ? Math.max(0, server.repairCompletesAt - gameTime)
                          : 0;
                        const repairPct = isRepairing ? Math.min(100, ((SERVER_REPAIR_TICKS - ticksLeft) / SERVER_REPAIR_TICKS) * 100) : 0;
                        return (
                          <div key={server.id} className="flex items-center justify-between bg-gray-800 p-2 rounded">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm truncate">{st?.name ?? 'Server'}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${isRepairing ? 'bg-amber-700 text-amber-100' : 'bg-red-700 text-red-100'}`}>
                                  {isRepairing ? 'REPAIRING' : 'FAILED'}
                                </span>
                              </div>
                              {isRepairing && (
                                <>
                                  <div className="w-full bg-gray-600 rounded-full h-1.5 mt-1">
                                    <div className="bg-amber-500 h-1.5 rounded-full transition-all" style={{ width: `${repairPct}%` }} />
                                  </div>
                                  <div className="text-[10px] text-amber-400 mt-0.5">
                                    {ticksLeft > 0 ? `~${Math.ceil(ticksLeft / 60)}min remaining` : 'Completing…'}
                                  </div>
                                </>
                              )}
                            </div>
                            {!isRepairing && selectedRack !== null && (
                              <button
                                onClick={() => repairServer(currentLocation, selectedRack, server.id)}
                                disabled={money < repairCost}
                                className={`px-3 py-1 rounded text-sm ${money >= repairCost ? 'bg-amber-600 hover:bg-amber-500 text-white' : 'bg-gray-600 text-gray-400 cursor-not-allowed'}`}
                                type="button"
                              >
                                🔧 Repair ${repairCost.toLocaleString()}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Server install panel — only when TOR switch is present */}
              {installedSwitch && (
                <>
                  <h4 className="text-md font-semibold mb-2">Install Servers</h4>
                  {locationConnectivity === 0 && (
                    <div className="mb-3 p-2 rounded bg-red-900/50 border border-red-700 text-red-300 text-xs">
                      <strong>No internet connectivity.</strong> Servers are installed but earn $0 revenue. Build the MMR and order at least one transit link or IXP connection to carry customer traffic.
                    </div>
                  )}
                  {locationConnectivity > 0 && locationConnectivity < 1 && (
                    <div className="mb-3 p-2 rounded bg-amber-900/50 border border-amber-700 text-amber-300 text-xs">
                      <strong>Underprovisioned uplink ({Math.round(locationConnectivity * 100)}% capacity).</strong> Revenue is proportionally reduced. Order more transit bandwidth or a higher-speed IXP port.
                    </div>
                  )}
                  {freeU <= 0 ? (
                    <div className="text-yellow-500 text-sm">Rack is physically full (48U used).</div>
                  ) : freePorts <= 0 ? (
                    <div className="text-yellow-500 text-sm">All {portCount} switch ports are in use. Servers need a free network port — this rack is at capacity.</div>
                  ) : (
                    <div className="space-y-3">
                      {serverTypes.map(serverType => {
                        const isLocked = serverType.unlockLevel > playerLevel;
                        const unlockAt = SERVER_UNLOCK_THRESHOLDS[serverType.unlockLevel] ?? 0;
                        const contract = CONTRACT_TYPES.find(c => c.id === serverType.contractCategory);
                        return (
                          <div key={serverType.id} className={`flex items-center justify-between p-3 rounded ${isLocked ? 'bg-gray-800/60 opacity-60' : 'bg-gray-700'}`}>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{serverType.name}</span>
                                {contract && (
                                  <span
                                    className={`text-[10px] px-1.5 py-0.5 rounded bg-gray-900/60 font-semibold ${contract.color}`}
                                    title={`${contract.description} SLA downtime penalty: ${Math.round(contract.slaDowntimePenaltyPct * 100)}%`}
                                  >
                                    {contract.shortLabel}
                                  </span>
                                )}
                                {isLocked && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-600 text-gray-300 font-semibold">
                                    🔒 Need {unlockAt} servers
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-400">{serverType.description}</div>
                              <div className="mt-1 flex gap-3 text-xs flex-wrap">
                                <span className="text-green-400">${serverType.cost.toLocaleString()}</span>
                                <span className="text-blue-400">{serverType.uSize}U</span>
                                <span className="text-red-400">Power: {serverType.powerDraw} kW</span>
                                <span className="text-cyan-400">Cooling: {serverType.coolingNeeded} kW</span>
                                <span className="text-yellow-400">Rev: ${serverType.revenue.toLocaleString()}/day</span>
                                <span className="text-violet-400" title={`NIC: ${serverType.nicSpeedGbps}G — typical traffic range`}>Traffic: {(serverType.trafficGbps * 0.5).toFixed(2)}–{(serverType.trafficGbps * 1.15).toFixed(2)} Gbps</span>
                                {(() => {
                                  const perfMult = 1 + (serverPerformanceUpgrade * 0.15);
                                  const ixpMult = 1 + ixpBonusFraction;
                                  const adjustedRevenue = serverType.revenue * perfMult * ixpMult * locationConnectivity;
                                  const powerCostDay = serverType.powerDraw * powerCostPerKwh * 24;
                                  const netDay = adjustedRevenue - powerCostDay;
                                  if (netDay <= 0) return <span className="text-red-400">No payback</span>;
                                  const days = Math.ceil(serverType.cost / netDay);
                                  const tooltip = [
                                    `Base rev: $${serverType.revenue.toLocaleString()}/day`,
                                    serverPerformanceUpgrade > 0 ? `+${Math.round(serverPerformanceUpgrade * 15)}% perf upgrade` : '',
                                    ixpBonusFraction > 0 ? `+${Math.round(ixpBonusFraction * 100)}% IXP bonus` : '',
                                    locationConnectivity < 1 ? `×${Math.round(locationConnectivity * 100)}% connectivity` : '',
                                    `Power: $${powerCostDay.toFixed(0)}/day`,
                                  ].filter(Boolean).join(' · ');
                                  return <span className="text-gray-400" title={tooltip}>~{days}d payback</span>;
                                })()}
                                {contract && <span className={contract.color}>{contract.customersPerServer} {contract.name} clients</span>}
                              </div>
                            </div>
                            <div className="relative">
                              {isLocked ? (
                                <span className="px-3 py-1 rounded text-sm bg-gray-700 text-gray-500 cursor-not-allowed">Locked</span>
                              ) : (() => {
                                const hasEnoughMoney = money >= serverType.cost;
                                const hasUSpace = freeU >= serverType.uSize;
                                const availablePowerNum = Math.max(0, availablePower);
                                const hasLocationPower = availablePowerNum >= serverType.powerDraw;
                                const rackBudget = selectedRackType?.power ?? 0;
                                const rackUsed = selectedRackUsedPower;
                                const rackRemaining = Math.max(0, rackBudget - rackUsed);
                                const hasRackPower = rackBudget > 0 && rackRemaining >= serverType.powerDraw;
                                const hasFreePort = freePorts > 0;

                                let buttonText = 'Install';
                                let buttonClass = 'px-3 py-1 rounded text-sm w-full ';
                                let isDisabled = true;

                                if (!hasFreePort) {
                                  buttonText = `🔌 No ports free`;
                                  buttonClass += 'bg-red-700 text-white cursor-not-allowed';
                                } else if (!hasRackPower) {
                                  buttonText = `⚡ PDU (${rackRemaining.toFixed(1)}kW left)`;
                                  buttonClass += 'bg-red-700 text-white cursor-not-allowed';
                                } else if (!hasLocationPower) {
                                  buttonText = `⚡ No grid power`;
                                  buttonClass += 'bg-red-600 text-white cursor-not-allowed';
                                } else if (!hasUSpace) {
                                  buttonText = `📦 Need ${serverType.uSize}U (${freeU}U free)`;
                                  buttonClass += 'bg-yellow-600 text-white cursor-not-allowed';
                                } else if (!hasEnoughMoney) {
                                  buttonText = `💰 Need $${(serverType.cost - money).toLocaleString()}`;
                                  buttonClass += 'bg-gray-600 text-white cursor-not-allowed';
                                } else {
                                  buttonText = '✅ Install';
                                  buttonClass += 'bg-green-600 hover:bg-green-700 text-white cursor-pointer';
                                  isDisabled = false;
                                }

                                return (
                                  <div className="flex flex-col gap-1">
                                    <button
                                      onClick={isDisabled ? undefined : () => installServer(currentLocation, selectedRack, serverType.id)}
                                      disabled={isDisabled}
                                      className={buttonClass}
                                      title={`Rack: ${rackUsed.toFixed(2)}/${rackBudget} kW · ${usedU}/${48}U used`}
                                      type="button"
                                    >
                                      {buttonText}
                                    </button>
                                    {!isDisabled && selectedRack !== null && (
                                      <button
                                        onClick={() => fillRack(currentLocation, selectedRack, serverType.id)}
                                        className="px-3 py-1 rounded text-sm bg-blue-700 hover:bg-blue-600 text-white w-full"
                                        title="Fill rack with as many of this server type as possible"
                                        type="button"
                                      >
                                        Fill rack
                                      </button>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {!selectedRack && (
        <div className="text-center text-gray-500 py-10">
          <p>Select a rack from the floor plan to manage it.</p>
        </div>
      )}
    </div>
  );
};

export default ServerManagement;
